import { asegurarNoNegativo } from './dinero';

/**
 * Importe en letras, para el recibo: "UN MILLÓN QUINIENTOS MIL GUARANÍES".
 *
 * Un recibo en Paraguay se lee en letras además de en números; es lo que se verifica
 * cuando hay un reclamo. Por eso vive en el dominio con sus tests y no en una plantilla.
 */

const UNIDADES = [
  '', 'UNO', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE',
  'DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISÉIS', 'DIECISIETE',
  'DIECIOCHO', 'DIECINUEVE', 'VEINTE', 'VEINTIUNO', 'VEINTIDÓS', 'VEINTITRÉS',
  'VEINTICUATRO', 'VEINTICINCO', 'VEINTISÉIS', 'VEINTISIETE', 'VEINTIOCHO', 'VEINTINUEVE',
];
const DECENAS = ['', '', '', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
const CENTENAS = [
  '', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS',
  'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS',
];

function menorQueMil(n: number): string {
  if (n === 0) return '';
  if (n === 100) return 'CIEN';
  const centena = Math.floor(n / 100);
  const resto = n % 100;
  const partes: string[] = [];
  if (centena > 0) partes.push(CENTENAS[centena]!);
  if (resto > 0) {
    if (resto < 30) partes.push(UNIDADES[resto]!);
    else {
      const decena = Math.floor(resto / 10);
      const unidad = resto % 10;
      partes.push(unidad > 0 ? `${DECENAS[decena]} Y ${UNIDADES[unidad]}` : DECENAS[decena]!);
    }
  }
  return partes.join(' ');
}

/** "UNO" se apocopa a "UN" delante de MIL, MILLÓN y del sustantivo de la moneda. */
function apocopar(texto: string): string {
  return texto.replace(/VEINTIUNO$/, 'VEINTIÚN').replace(/UNO$/, 'UN');
}

export function numeroEnLetras(numero: number): string {
  asegurarNoNegativo(numero, 'El número');
  if (numero === 0) return 'CERO';

  const grupos: Array<[number, string, string]> = [
    [1_000_000_000_000, 'BILLÓN', 'BILLONES'],
    [1_000_000, 'MILLÓN', 'MILLONES'],
  ];

  let restante = numero;
  const partes: string[] = [];
  for (const [divisor, singular, plural] of grupos) {
    const cantidad = Math.floor(restante / divisor);
    restante %= divisor;
    if (cantidad === 0) continue;
    partes.push(cantidad === 1 ? `UN ${singular}` : `${apocopar(miles(cantidad))} ${plural}`);
  }
  if (restante > 0) partes.push(miles(restante));
  return partes.join(' ');
}

function miles(n: number): string {
  const millares = Math.floor(n / 1000);
  const resto = n % 1000;
  const partes: string[] = [];
  if (millares === 1) partes.push('MIL');
  else if (millares > 1) partes.push(`${apocopar(menorQueMil(millares))} MIL`);
  if (resto > 0) partes.push(menorQueMil(resto));
  return partes.join(' ');
}

const NOMBRE_MONEDA: Record<string, [string, string]> = {
  PYG: ['GUARANÍ', 'GUARANÍES'],
  USD: ['DÓLAR', 'DÓLARES'],
  BRL: ['REAL', 'REALES'],
  ARS: ['PESO', 'PESOS'],
};

/** Decimales de cada moneda. El guaraní no tiene; el dólar sí. */
export const DECIMALES_MONEDA: Record<string, number> = { PYG: 0, USD: 2, BRL: 2, ARS: 2 };

export function decimalesDe(moneda: string): number {
  return DECIMALES_MONEDA[moneda] ?? 0;
}

export function montoEnLetras(monto: number, moneda = 'PYG'): string {
  asegurarNoNegativo(monto, 'El monto');
  const [singular, plural] = NOMBRE_MONEDA[moneda] ?? [moneda, moneda];
  const decimales = decimalesDe(moneda);
  const entero = decimales > 0 ? Math.floor(monto / 10 ** decimales) : monto;
  const centavos = decimales > 0 ? monto % 10 ** decimales : 0;

  const texto = entero === 1 ? `UN ${singular}` : `${apocopar(numeroEnLetras(entero))} ${plural}`;
  if (decimales === 0 || centavos === 0) return texto;
  return `${texto} CON ${String(centavos).padStart(decimales, '0')}/${10 ** decimales}`;
}
